import React, { useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import SearchIcon from "@atlaskit/icon/glyph/search";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Initializer } from "../../../../analytics/atl-analytics/initializer";
import { Cross } from "../../../../libraries/wpl/foundations/src/";
import { TextInput } from "../../../wpl/text-input/src";

const displayNone = css({
    [media.below.sm]: {
        display: "none"
    }
});

const searchIconStyle = css({
    "background": "transparent",
    "border": 0,
    "cursor": "pointer",
    "padding": 0,
    "color": "#64686f",
    "&:hover": {
        svg: {
            backgroundColor: "#dfe1e5"
        }
    },
    "svg": {
        color: "#64686f",
        borderRadius: "50%",
        height: "auto",
        padding: "5px",
        width: "32px",
        [media.below.md]: {
            width: "50px"
        }
    }
});

const searchMainContainerStyle = css({
    insetBlockStart: "100%",
    insetInlineStart: token("space.0"),
    backgroundColor: "#f4f5f7",
    lineHeight: token("space.300"),
    position: "absolute",
    width: "100%",
    zIndex: 11,
    borderBlockEnd: "0.5px solid #b3bac5"
});

const searchContainerStyle = css({
    paddingBlockStart: token("space.150"),
    textAlign: "center",
    margin: `${token("space.0")} auto`,
    [media.below.md]: {
        paddingBlockStart: 0,
        width: "100%"
    }
});

const searchInputContainerStyle = css({
    display: "inline-block",
    marginInlineEnd: token("space.100"),
    textAlign: "left",
    width: "calc(66.66666% - 91px)",
    [media.below.md]: {
        "div label": {
            display: "none"
        },
        "width": "calc(100% - 60px)"
    }
});

const searchFormStyle = css({
    lineHeight: token("space.300"),
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "5px 20px 24px 20px",
    position: "relative",
    label: {
        marginInlineStart: 0,
        fontWeight: 400,
        fontSize: "0.875rem",
        marginBlockEnd: token("space.050"),
        display: "block"
    },
    [media.below.md]: {
        padding: `${token("space.200")} ${token("space.250")}`,
        textAlign: "center"
    }
});

const closePanelIconContainerStyle = css({
    background: "transparent",
    border: 0,
    cursor: "pointer",
    display: "flex",
    padding: 0,
    height: token("space.200"),
    position: "absolute",
    insetInlineEnd: token("space.250"),
    width: token("space.200"),
    insetBlockStart: token("space.200"),
    [media.below.md]: {
        "background": "rgba(216, 216, 216, 0.6)",
        "height": "100vh",
        "insetInlineStart": 0,
        "insetBlockStart": "100%",
        "width": "100%",
        "> svg": {
            display: "none"
        }
    }
});

const searchIconButtonStyle = css({
    "backgroundColor": "#0052cc",
    "backgroundSize": token("space.200"),
    "border": "1px solid #0052cc",
    "borderRadius": token("space.050"),
    "cursor": "pointer",
    "height": token("space.400"),
    "verticalAlign": "bottom",
    "color": "#ffffff",
    "maxWidth": token("space.500"),
    "padding": `${token("space.0")} ${token("space.100")}`,
    "marginBlockEnd": token("space.075"),
    "svg": {
        color: "#ffffff"
    },
    "&:visited": {
        color: "#f4f5f7"
    },
    "&:hover, &:active": {
        backgroundColor: "#0049b0",
        border: "1px solid #0049b0"
    }
});

const pageBlurCss = css({
    background: "rgba(216, 216, 216, 0.6)",
    border: 0,
    display: "block",
    height: "100vh",
    insetInlineStart: 0,
    position: "absolute",
    insetBlockStart: "100%",
    width: "100%",
    zIndex: 1
});

export interface SearchProps {
    searchLabel?: string;
    className?: string;
    placeholder?: string;
    searchUrl?: string;
    baseUrl?: string;
    isOpen: boolean;
    openSearch: () => void;
    closeSearch: () => void;
    testId?: string;
}

export const Search = ({
    searchLabel,
    className,
    placeholder,
    searchUrl,
    testId,
    isOpen = false,
    openSearch,
    closeSearch
}: SearchProps) => {
    const [searchText, setSearchText] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    const focusOnInput = () => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    useEffect(() => {
        Initializer.autoTrack();

        if (isOpen) {
            focusOnInput();
        }
    }, [isOpen]);

    const intl = useIntl();
    const translated = {
        searchLabel: intl.formatMessage({
            id: "globalNav.search.label",
            defaultMessage: "Search"
        }),
        submitSearch: intl.formatMessage({
            id: "globalNav.search.submit",
            defaultMessage: "Submit"
        }),
        closeSearch: intl.formatMessage({
            id: "globalNav.search.close",
            defaultMessage: "Close"
        })
    };

    return (
        <React.Fragment>
            <button
                css={[
                    searchIconStyle,
                    // hide search icon on mobile when search is open
                    isOpen && displayNone
                ]}
                onClick={(event) => {
                    event.preventDefault();
                    focusOnInput();
                    openSearch();
                }}
                id="global-nav-search-icon"
                aria-label={translated.searchLabel}
                data-testid={testId || "global-nav-search-icon"}
                data-event="clicked"
                data-event-component="linkButton"
                data-event-container="globalNavWac"
                data-product-key="jiraSoftware"
            >
                <SearchIcon label={translated.searchLabel} />
            </button>
            {isOpen && (
                <React.Fragment>
                    <div
                        css={searchMainContainerStyle}
                        className={className}
                        id="global-nav-wac-search"
                        data-testid="search-container"
                    >
                        <form
                            css={searchFormStyle}
                            action={searchUrl || `/search`}
                            method="GET"
                            id="global-nav-wac-search-form"
                            data-testid="search-form"
                        >
                            <div
                                id="global-nav-wac-search-container"
                                css={searchContainerStyle}
                            >
                                <div
                                    css={searchInputContainerStyle}
                                    id="global-nav-wac-search-input-container"
                                >
                                    <TextInput
                                        ref={inputRef}
                                        className="fullWidth"
                                        label={
                                            searchLabel ||
                                            `${translated.searchLabel} atlassian.com`
                                        }
                                        name="q"
                                        id="global-nav-search-input"
                                        value={searchText}
                                        placeholder={
                                            placeholder ||
                                            translated.searchLabel
                                        }
                                        onChangeHandler={setSearchText}
                                        data-testid="search-input"
                                        aria-label="Search Input"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    id="global-nav-search-submit"
                                    form="global-nav-wac-search-form"
                                    css={searchIconButtonStyle}
                                    data-testid="search-submit"
                                    aria-label={translated.submitSearch}
                                    data-event="clicked"
                                    data-event-component="linkButton"
                                    data-event-container="globalNav"
                                >
                                    <SearchIcon
                                        label={translated.submitSearch}
                                    />
                                </button>
                                <button
                                    id="global-nav-search-close"
                                    css={closePanelIconContainerStyle}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        return closeSearch();
                                    }}
                                    data-testid="search-close"
                                    aria-label={translated.closeSearch}
                                    data-event="clicked"
                                    data-event-component="linkButton"
                                    data-event-container="globalNav"
                                >
                                    <Cross size="16" />
                                </button>
                            </div>
                        </form>
                    </div>
                    <button
                        aria-label={translated.closeSearch}
                        css={pageBlurCss}
                        onClick={() => closeSearch()}
                        data-event="clicked"
                        data-event-component="linkButton"
                        data-event-container="globalNav"
                    />
                </React.Fragment>
            )}
        </React.Fragment>
    );
};
